import { useState, type ChangeEvent, type FormEvent } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, DataTable, EmptyState, Input, LoadingState, Select, type Column } from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { useCurrentOrgStore } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import { getOAuthCapabilities, listOAuthAudit, type AuditEvent, type AuditFilters } from "../lib/api/oauth";

const EVENTS = [
  "client_registered", "registration_rejected", "consent_shown", "consent_granted", "consent_denied", "token_issued", "token_denied",
  "refresh_reuse_detected", "code_reuse_detected", "grant_revoked", "token_revoked", "introspect", "resource_created", "resource_updated",
  "resource_deleted", "introspection_client_provisioned", "agent_created", "agent_updated", "agent_disabled", "agent_enabled",
  "agent_secret_rotated", "grant_org_dropped", "org_policy_updated", "client_verified", "client_unverified", "client_revoked", "client_deleted",
  "operator_client_created", "operator_client_updated", "operator_client_secret_rotated", "operator_client_disabled", "operator_client_enabled", "operator_client_deleted",
];

interface FormState {
  client_id: string;
  user_id: string;
  agent_id: string;
  resource: string;
  organization_id: string;
  event: string;
  outcome: string;
  from: string; // datetime-local
  to: string;
}
const emptyForm: FormState = { client_id: "", user_id: "", agent_id: "", resource: "", organization_id: "", event: "", outcome: "", from: "", to: "" };

function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toFilters(f: FormState): AuditFilters {
  return {
    client_id: f.client_id.trim(), user_id: f.user_id.trim(), agent_id: f.agent_id.trim(), resource: f.resource.trim(),
    organization_id: f.organization_id.trim(), event: f.event, outcome: f.outcome, from: toIso(f.from), to: toIso(f.to), limit: 50,
  };
}

function outcomeBadge(o: string) {
  return <Badge variant={o === "ok" ? "success" : o === "denied" ? "warning" : "danger"}>{o}</Badge>;
}

export function OAuthAuditRoute() {
  const user = useAuthStore((s) => s.user);
  const memberships = useCurrentOrgStore((st) => st.memberships);
  const orgNames: Record<string, string> = {};
  for (const m of memberships) orgNames[m.organization.id] = m.organization.name;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [applied, setApplied] = useState<AuditFilters>(toFilters(emptyForm));

  const capsQuery = useQuery({ queryKey: ["oauth-capabilities"], queryFn: getOAuthCapabilities, enabled: !!user });
  const allowed = capsQuery.data?.view_audit === true;
  const isAdmin = capsQuery.data?.platform_admin === true;

  const auditQuery = useInfiniteQuery({
    queryKey: ["oauth-audit", applied],
    queryFn: ({ pageParam }) => listOAuthAudit({ ...applied, cursor: pageParam || undefined }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor || undefined,
    enabled: allowed,
  });

  if (!user) return <EmptyState title="Not signed in" description="Sign in to view the OAuth audit." />;
  if (capsQuery.isLoading) return <LoadingState message="Loading..." />;
  if (!allowed) {
    return <EmptyState title="Owner or admin required" description="The OAuth audit is visible to organization owners and admins, and to platform admins." />;
  }

  const events = auditQuery.data?.pages.flatMap((p) => p.events) ?? [];
  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });

  const columns: Column<AuditEvent>[] = [
    { key: "at", label: "Time", render: (e) => new Date(e.at).toLocaleString() },
    { key: "event", label: "Event", render: (e) => <span className="font-mono text-xs">{e.event}</span> },
    { key: "outcome", label: "Outcome", render: (e) => outcomeBadge(e.outcome) },
    {
      key: "who",
      label: "Who",
      render: (e) => (
        <div className="text-xs">
          <div>{e.principal_type}</div>
          {e.user_email ? <div>{e.user_email}</div> : e.user_id ? <div className="font-mono">{e.user_id}</div> : null}
          {e.agent_id && <div className="font-mono">{e.agent_id}</div>}
        </div>
      ),
    },
    { key: "client", label: "Client", render: (e) => <span className="font-mono text-xs">{e.client_id ?? ""}</span> },
    { key: "resource", label: "Resource", render: (e) => <span className="break-all font-mono text-xs">{e.resource ?? ""}</span> },
    {
      key: "orgs",
      label: "Organizations",
      render: (e) => {
        const ids = e.organization_ids?.length ? e.organization_ids : e.organization_id ? [e.organization_id] : [];
        return (
          <ul className="text-xs">
            {ids.map((id) => (
              <li key={id} className="font-mono">
                {orgNames[id] ? `${orgNames[id]} (${id.slice(0, 8)})` : id}
              </li>
            ))}
          </ul>
        );
      },
    },
    { key: "scopes", label: "Scopes", render: (e) => <span className="font-mono text-xs">{e.scopes.join(" ")}</span> },
    { key: "detail", label: "Detail", render: (e) => <span className="text-xs">{e.detail ?? ""}</span> },
    ...(isAdmin ? [{ key: "ip", label: "IP", render: (e: AuditEvent) => <span className="font-mono text-xs">{e.ip ?? ""}</span> }] : []),
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">OAuth audit</h1>
      <p className="text-sm text-[var(--color-neutral-500)]">
        Who was given or denied access, and every administrative change, newest first.
        {isAdmin ? " You see every event." : " You see events for the organizations you own or administer, without addresses."} Look
        for <span className="font-mono">refresh_reuse_detected</span>, <span className="font-mono">code_reuse_detected</span> and
        rising <span className="font-mono">token_denied</span> counts.
      </p>

      <Card padding="lg">
        <CardBody>
          <form
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setApplied(toFilters(form));
            }}
            aria-label="Audit filters"
          >
            <Input label="Client ID" value={form.client_id} onChange={set("client_id")} />
            <Input label="User ID" value={form.user_id} onChange={set("user_id")} />
            <Input label="Agent ID" value={form.agent_id} onChange={set("agent_id")} />
            <Input label="Resource URL" value={form.resource} onChange={set("resource")} />
            <Input label="Organization ID (matches any organization of an event)" value={form.organization_id} onChange={set("organization_id")} />
            <Select label="Event" value={form.event} placeholder="Any event" options={EVENTS.map((v) => ({ value: v, label: v }))} onChange={set("event")} />
            <Select
              label="Outcome"
              value={form.outcome}
              placeholder="Any outcome"
              options={["ok", "denied", "error"].map((v) => ({ value: v, label: v }))}
              onChange={set("outcome")}
            />
            <Input label="From" type="datetime-local" value={form.from} onChange={set("from")} />
            <Input label="To" type="datetime-local" value={form.to} onChange={set("to")} />
            <div className="flex items-end gap-2">
              <Button type="submit">Apply filters</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setForm(emptyForm);
                  setApplied(toFilters(emptyForm));
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {auditQuery.isLoading ? (
        <LoadingState message="Loading events..." />
      ) : auditQuery.isError ? (
        <Alert variant="danger" title="Failed to load events">
          {apiErrorMessage(auditQuery.error)}
        </Alert>
      ) : events.length === 0 ? (
        <EmptyState title="No events" description="Nothing matches these filters." />
      ) : (
        <>
          <Card padding="none">
            <DataTable<AuditEvent> data={events} columns={columns} emptyMessage="No events." />
          </Card>
          <div className="flex items-center gap-3">
            <Button variant="secondary" disabled={!auditQuery.hasNextPage || auditQuery.isFetchingNextPage} onClick={() => auditQuery.fetchNextPage()}>
              {auditQuery.isFetchingNextPage ? "Loading..." : auditQuery.hasNextPage ? "Load older events" : "No older events"}
            </Button>
            <span className="text-sm text-[var(--color-neutral-500)]" aria-live="polite">
              {events.length} event(s) shown
            </span>
          </div>
        </>
      )}
    </div>
  );
}
